import {
  Box,
  Button,
  Container,
  Fab,
  Grid,
  List,
  ListItem,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

import { TeamView } from "../../model/Dto/TeamView";

import {
  useSelectedFightersDispatch,
  useSelectedFightersState,
} from "../../providers/SelectedFightersProvider";
import { FC, memo, useEffect, useMemo, useState } from "react";
import { FighterView, Status } from "../../model/Types";
import { StatsTable } from "./StatsTable";
import { WeaponsTable } from "../../components/FighterCard/WeaponsTable";
import { FighterCardHeader } from "../../components/FighterCard/FighterCardHeader";
import { FighterCard } from "../../components/FighterCard/FighterCard";
import cardBackground from "../../backgrounds/card_background.jpg";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContainerWithCircularProgress from "../../components/ContainerWithCircularProgress";
import { alpha } from "@mui/material";

// DnD imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Api } from "../../request/api/api";
import { FighterOrderDto } from "../../model/Dto/FighterOrderDto";

interface Props {
  teamView: TeamView | undefined;
  openAddFighterDialog: () => void;
  teamId: string;
  fetchData: () => void;
}

export default function FighterCardList({
  teamView,
  openAddFighterDialog,
  teamId,
  fetchData,
}: Props) {
  const [isDragging, setIsDragging] = useState(false);
  const fightersSelectionInfo = useSelectedFightersState().fighters;
  const selectedFightersReducer = useSelectedFightersDispatch();
  const selectedFightersCost = fightersSelectionInfo.reduce(
    (partialSum, a) => partialSum + (a.isSelected ? a.cost : 0),
    0
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Минимальное расстояние перед началом перетаскивания
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (teamView !== undefined) {
      selectedFightersReducer({
        type: "update",
        newState: {
          fighters: teamView.fighters.map((fighter) => ({
            id: fighter.id,
            isSelected: false,
            cost: fighter.totalCost,
          })),
        },
      });
    }
  }, [teamView]);

  const selectedFightersIds = useMemo(() => {
    return fightersSelectionInfo
      .filter((fighter) => fighter.isSelected)
      .map((info) => info.id);
  }, [fightersSelectionInfo]);

  const handleDragEnd = async (event: DragEndEvent) => {
    setIsDragging(false);
    const { active, over } = event;

    if (over && active.id !== over.id && teamView) {
      const oldIndex = teamView.fighters.findIndex(
        (fighter) => fighter.id === active.id
      );
      const newIndex = teamView.fighters.findIndex(
        (fighter) => fighter.id === over.id
      );

      // Создаем новый массив с измененным порядком
      const newFighters = arrayMove(teamView.fighters, oldIndex, newIndex);
      
      // Временно обновляем UI для плавности
      setTeamViewLocal({
        ...teamView,
        fighters: newFighters,
      });

      // Отправляем запрос на сервер

      const orderRequest: FighterOrderDto[] = newFighters.map((fighter, index) => ({
        fighterId: fighter.id,
        order: index,
      }));

      // Отправляем запрос на сервер используя функцию SaveOrder
      const success = await Api.team.saveOrder({
        teamId: teamId,
        fighters: orderRequest,
      });

      if (success) {
        // Обновляем данные с сервера
        fetchData();
      } else {
        // В случае ошибки возвращаем исходный порядок
        setTeamViewLocal(teamView);
        // Можно показать уведомление об ошибке
      }
    }
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  // Локальное состояние для плавного обновления UI во время перетаскивания
  const [teamViewLocal, setTeamViewLocal] = useState(teamView);
  
  useEffect(() => {
    setTeamViewLocal(teamView);
  }, [teamView]);

  const currentTeamView = teamViewLocal || teamView;

  return (
    <Box data-fighter-card-list>
      <List>
        {!currentTeamView ? (
          <ContainerWithCircularProgress height="400px" />
        ) : currentTeamView.fighters.length === 0 ? (
          <Container>
            <Box maxWidth={400} sx={{ margin: "auto" }}>
              <Typography
                align="center"
                variant="body1"
                color="white"
                gutterBottom>
                В этой банде нет бойцов.
              </Typography>
              {currentTeamView.availableForEdit ? (
                <Stack>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={openAddFighterDialog}
                    startIcon={<AddIcon />}>
                    Добавить бойца
                  </Button>
                </Stack>
              ) : (
                <></>
              )}
            </Box>
          </Container>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}>
            <SortableContext
              items={currentTeamView.fighters.map((f) => f.id)}
              strategy={verticalListSortingStrategy}>
              {selectedFightersIds.length > 0 && (
                <FighterCountButton
                  content={`${selectedFightersIds.length} ${
                    selectedFightersIds.length > 1 ? "бойца выбрано" : "боец выбран"
                  }. Суммарный рейтинг - ${selectedFightersCost}. Нажмите, чтобы сбросить выбор.`}
                  onClick={() => selectedFightersReducer({ type: "delete all" })}
                />
              )}
              
              {/* Выбранные бойцы */}
              {currentTeamView.fighters
                .filter((fighter) => selectedFightersIds.includes(fighter.id))
                .map((fighterView) => (
                  <SortableFighterCardItem
                    key={fighterView.id}
                    fighterView={fighterView}
                    isSelected={true}
                    availableForEdit={currentTeamView.availableForEdit}
                    isDragging={isDragging}
                  />
                ))}

              {/* Разделитель, если есть и выбранные, и невыбранные */}
              {selectedFightersIds.length > 0 &&
                selectedFightersIds.length < currentTeamView.fighters.length && (
                  <FighterCountButton
                    content="Невыбранные бойцы. Нажмите, чтобы сбросить выбор."
                    onClick={() => selectedFightersReducer({ type: "delete all" })}
                  />
                )}

              {/* Невыбранные бойцы */}
              {currentTeamView.fighters
                .filter((fighter) => !selectedFightersIds.includes(fighter.id))
                .map((fighterView) => (
                  <SortableFighterCardItem
                    key={fighterView.id}
                    fighterView={fighterView}
                    isSelected={false}
                    availableForEdit={currentTeamView.availableForEdit}
                    isDragging={isDragging}
                  />
                ))}
            </SortableContext>
          </DndContext>
        )}
      </List>
    </Box>
  );
}

// Обновленный компонент карточки с поддержкой сортировки
interface SortableFighterCardItemProps {
  fighterView: FighterView;
  isSelected: boolean;
  availableForEdit: boolean;
  isDragging: boolean;
}

const SortableFighterCardItem: FC<SortableFighterCardItemProps> = memo(
  ({ fighterView, isSelected, availableForEdit, isDragging }) => {
    const selectedFightersReducer = useSelectedFightersDispatch();
    
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging: isSortableDragging,
    } = useSortable({ id: fighterView.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isSortableDragging ? 0.5 : 1,
      zIndex: isSortableDragging ? 999 : 'auto',
      position: 'relative' as const,
    };

    return (
      <ListItem
        ref={setNodeRef}
        style={style}
        sx={{
          '@media print': {
            pageBreakInside: 'avoid',
            breakInside: 'avoid-page',
          },
          cursor: isDragging ? 'grabbing' : 'auto',
          position: 'relative',
        }}>
        <FighterCard data-printable="true">
          <Box sx={{ display: 'flex', width: '100%', position: 'relative' }}>
            {/* Область захвата для перетаскивания - слева, но с улучшенным дизайном */}
            {availableForEdit && (
              <Box
                {...attributes}
                {...listeners}
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  displayPrint: 'none',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '48px',
                  minWidth: '48px',
                  cursor: isSortableDragging ? 'grabbing' : 'grab',
                  background: isSortableDragging 
                    ? alpha('#ffd700', 0.3) 
                    : alpha('#000000', 0.4),
                  borderRight: '2px solid',
                  borderImage: 'linear-gradient(180deg, #ffd700 0%, #b8860b 50%, #ffd700 100%) 1',
                  mr: 1.5,
                  borderRadius: '15px 0 0 15px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: alpha('#ffd700', 0.25),
                    '& .drag-icon': {
                      color: '#ffd700',
                      transform: 'scale(1.1)',
                    },
                  },
                  '&:active': {
                    background: alpha('#ffd700', 0.35),
                    cursor: 'grabbing',
                  },
                }}>
                <DragIndicatorIcon 
                  className="drag-icon"
                  sx={{ 
                    color: isSortableDragging ? '#ffd700' : '#ffd700',
                    fontSize: '28px',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                    transition: 'all 0.2s ease',
                  }} 
                />
              </Box>
            )}
            
            <Box sx={{ flex: 1 }}>
              <FighterCardHeader
                name={fighterView.name}
                rang={fighterView.rang}
                totalCost={fighterView.totalCost}
                status={fighterView.status}
                isSelected={isSelected}
                onClick={() =>
                  selectedFightersReducer({
                    type: isSelected ? "delete" : "select",
                    id: fighterView.id,
                  })
                }
              />
              {fighterView.status !== Status.DEAD && (
                <>
                  <StatsTable
                    characteristics={fighterView.totalCharacteristics}
                    xp={fighterView.xp}
                    lvl={fighterView.lvl}
                  />
                  <ListItem disablePadding sx={{ mb: "10px" }}>
                    <WeaponsTable weapons={fighterView.weapons} />
                  </ListItem>
                  <ListItem disablePadding>
                    <Grid container spacing={1}>
                      <GridStroke
                        name="Экипировка"
                        items={fighterView.equipment.map((equipment) => equipment.name)}
                      />
                      <GridStroke
                        name="Навыки"
                        items={fighterView.skills.map((equipment) => equipment.name)}
                      />
                    </Grid>
                  </ListItem>
                </>
              )}
            </Box>
          </Box>
          
          <RouterLink to={`/fighter/${fighterView.id}`}>
            <Box
              sx={{
                displayPrint: 'none',
                backgroundColor: "#343a40",
                position: "absolute",
                right: "-15px",
                bottom: "-15px",
                borderRadius: "50%",
                border: "2px solid #747474",
                zIndex: 10,
              }}>
              <Fab size="medium" aria-label="add">
                {availableForEdit ? <EditIcon /> : <VisibilityIcon />}
              </Fab>
            </Box>
          </RouterLink>
        </FighterCard>
      </ListItem>
    );
  }
);

// Вспомогательные компоненты остаются без изменений
interface FighterCountButtonProps {
  content: string;
  onClick: () => void;
}

const FighterCountButton: FC<FighterCountButtonProps> = ({
  content,
  onClick,
}) => {
  return (
    <ListItem sx={{ justifyContent: "center", maxWidth: 900 }}>
      <Button
        onClick={onClick}
        variant="outlined"
        style={{ backgroundColor: "white", textTransform: "none" }}
        sx={{
          backgroundImage: `url('${cardBackground}')`,
          backgroundRepeat: "repeat-y",
          boxShadow:
            "2px 2px 5px 3px rgb(0 0 0 / 50%), -2px -2px 5px 3px rgb(0 0 0 / 50%)",
          borderRadius: "10px",
          paddingX: "10px",
          paddingY: "4px",
        }}>
        <Typography>{content}</Typography>
      </Button>
    </ListItem>
  );
};

interface GridStrokeProps {
  name: string;
  items: string[];
}

const GridStroke: FC<GridStrokeProps> = memo(({ name, items }) => {
  return (
    <>
      {items.length > 0 ? (
        <>
          <Grid item xs={6} sm={2}>
            <Typography component={"span"} variant="body1">
              {name}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={10}>
            <Typography component={"span"} variant="body1">
              {items.join(", ")}
            </Typography>
          </Grid>
        </>
      ) : (
        <></>
      )}
    </>
  );
});

